// Navigation utility for managing dynamic breadcrumbs and active menu states
export interface NavigationSource {
  source: 'builder' | 'properties';
  title: string;
  breadcrumbs: Array<{ label: string; path?: string }>;
  activeMenuId: string;
}

export const navigationConfigs: Record<string, NavigationSource> = {
  builder: {
    source: 'builder',
    title: 'Listing Detail',
    breadcrumbs: [
      { label: 'Builder', path: '/builder' },
      { label: 'Builder Detail', path: '/builder-detail' },
      { label: 'Listing Detail' }
    ],
    activeMenuId: 'builder'
  },
  properties: {
    source: 'properties',
    title: 'Property Detail',
    breadcrumbs: [
      { label: 'Properties', path: '/properties' },
      { label: 'Property Detail' }
    ],
    activeMenuId: 'properties'
  }
};

export const detectNavigationSource = (
  searchParams: URLSearchParams, 
  pathname: string
): NavigationSource => {
  const source = searchParams.get('source');
  const from = searchParams.get('from');
  
  if (source === 'properties' || from === 'properties' || pathname.includes('/properties/')) {
    return navigationConfigs.properties ?? {
      source: 'properties',
      title: 'Property Detail',
      breadcrumbs: [{ label: 'Properties' }, { label: 'Property Detail' }],
      activeMenuId: 'properties'
    };
  }
  
  return navigationConfigs.builder ?? {
    source: 'builder',
    title: 'Listing Detail',
    breadcrumbs: [{ label: 'Builder' }, { label: 'Listing Detail' }],
    activeMenuId: 'builder'
  };
};

export const setNavigationContext = (config: NavigationSource) => {
  // Set document title
  document.title = `${config.title}`;
  
  // Set active menu for sidebar
  localStorage.setItem('activeMenu', config.activeMenuId);
  
  // Store navigation context for future reference
  sessionStorage.setItem('lastNavigationSource', config.source);
  sessionStorage.setItem('navigationConfig', JSON.stringify(config));
};

export const getStoredNavigationConfig = (): NavigationSource | null => {
  try {
    const stored = sessionStorage.getItem('navigationConfig');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// Hook for navigation management
export const useNavigationSource = () => {
  const getActiveMenu = (): string => {
    return localStorage.getItem('activeMenu') || 'builder';
  };
  
  const getLastNavigationSource = (): string => {
    return sessionStorage.getItem('lastNavigationSource') || 'builder';
  };
  
  return {
    getActiveMenu,
    getLastNavigationSource,
    getStoredNavigationConfig
  };
};